import { useTranslation } from "@sagot/i18n";
import { Badge, Icon } from "@sagot/ui";
import type { SourceView } from "../shared/types";
import { useAudienceLabel } from "../shared/use-shared-queries";
import { audienceStyle, primaryAudience } from "./source-model";

/** Access cell: most open audience as an icon + label badge (tenant label), "+n" for the rest. */
export function AccessBadge({ audiences }: { audiences: readonly number[] }) {
  const { t } = useTranslation();
  const labelOf = useAudienceLabel();
  const primary = primaryAudience(audiences);
  if (!primary) return <Badge tone="danger">{t("deployKnowledge.knowledge.table.noAccess")}</Badge>;
  const style = audienceStyle(primary.code);
  const others = audiences.filter((code) => code !== primary.code).map(labelOf);
  return (
    <span className="inline-flex items-center gap-1" title={others.length > 0 ? [labelOf(primary.code), ...others].join(", ") : undefined}>
      <Badge tone={style.tone}>
        <Icon name={style.icon} className="size-3" />
        {labelOf(primary.code)}
      </Badge>
      {primary.extra > 0 ? (
        <span className="text-[11px] font-semibold text-fg-subtle">
          {t("deployKnowledge.knowledge.table.more", { count: primary.extra })}
          <span className="sr-only">{`: ${others.join(", ")}`}</span>
        </span>
      ) : null}
    </span>
  );
}

const PUBLIC_AUDIENCE = 1;

/** "May contain email addresses": personal data was found in a source that guests can read. */
export function PiiChip({ source }: { source: Pick<SourceView, "audiences" | "pii_found"> }) {
  const { t } = useTranslation();
  if (source.pii_found.length === 0 || !source.audiences.includes(PUBLIC_AUDIENCE)) return null;
  return (
    <Badge tone="warning" title={t("deployKnowledge.knowledge.pii.title")} data-testid="pii-chip">
      <Icon name="alert-triangle" className="size-3" />
      {t("deployKnowledge.knowledge.pii.chip", { kinds: source.pii_found.join(", ") })}
    </Badge>
  );
}
